<template>
  <div>
    <div id="stripe-card-element" class="stripe-card-container mb-4" />

    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4"
    >
      {{ error }}
    </div>

    <div v-if="verifying" class="text-center py-6">
      <BaseMiniSpinner />
      <p class="mt-3 text-gray-600">Verifying payment...</p>
    </div>

    <div v-else class="flex gap-2 mt-2">
      <button
        type="button"
        :disabled="processing || !ready"
        class="flex-1 bg-green-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="submit"
      >
        <span v-if="processing">Processing...</span>
        <span v-else>Pay ${{ Number(amount).toFixed(2) }}</span>
      </button>
      <slot name="cancel" />
    </div>
  </div>
</template>

<script>
export default {
  name: "StripeCardSection",
  props: {
    stripe: { type: Object, required: true },
    clientSecret: { type: String, required: true },
    amount: { type: [String, Number], required: true },
    customerEmail: { type: String, default: "" },
    billingAddress: { type: Object, default: null }
  },
  data() {
    return {
      cardElement: null,
      ready: false,
      processing: false,
      verifying: false,
      error: null
    };
  },
  mounted() {
    this.mountCard();
  },
  beforeDestroy() {
    if (this.cardElement) {
      this.cardElement.unmount();
      this.cardElement = null;
    }
  },
  methods: {
    mountCard() {
      const elements = this.stripe.elements({
        appearance: { theme: "stripe" },
        clientSecret: this.clientSecret
      });
      this.cardElement = elements.create("card", { disableLink: true });
      this.cardElement.mount("#stripe-card-element");
      this.cardElement.on("ready", () => {
        this.ready = true;
      });
    },
    async submit() {
      if (this.processing || !this.ready) return;
      this.error = null;
      this.processing = true;

      try {
        const { error, paymentIntent } = await this.stripe.confirmCardPayment(
          this.clientSecret,
          {
            payment_method: {
              card: this.cardElement,
              billing_details: {
                email: this.customerEmail || undefined,
                name: this.billingAddress?.name || undefined,
                address: this.billingAddress
                  ? {
                      postal_code: this.billingAddress.postal_code || undefined,
                      country: this.billingAddress.country || undefined
                    }
                  : undefined
              }
            }
          }
        );

        if (error) {
          this.error = error.message;
          this.processing = false;
          return;
        }

        if (paymentIntent?.status === "succeeded") {
          this.verifying = true;
          this.$emit("confirmed", paymentIntent.id);
        } else {
          this.error = "Payment was not completed. Please try again.";
          this.processing = false;
        }
      } catch (e) {
        this.error = e.message || "An error occurred during payment";
        this.processing = false;
      }
    }
  }
};
</script>

<style lang="postcss" scoped>
.stripe-card-container {
  padding: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background-color: white;
}
</style>
